import { useState } from "react";
import { useApi } from "@/api/useApi";
import type { ReportSummary } from "@/api/types";
import { Icon } from "@/components/Icon";
import { StatusBadge } from "@/components/StatusBadge";
import { dateRange, money } from "@/format";
import { useDialogs } from "@/components/dialogs";

type Result = { ok: boolean; text: string } | undefined;

const message = (caught: unknown) => (caught instanceof Error ? caught.message : String(caught));

/**
 * Deletes a report for good, by its number: it's looked up first so the administrator sees which
 * report it is, then confirmed before anything goes.
 */
export function DeleteReportCard() {
  const api = useApi();
  const dialogs = useDialogs();
  const [typed, setTyped] = useState("");
  const [found, setFound] = useState<ReportSummary>();
  const [result, setResult] = useState<Result>();
  const [busy, setBusy] = useState(false);
  const number = Number(typed.trim().replace(/^#/, ""));
  const valid = Number.isInteger(number) && number > 0;

  return (
    <form
      className="card stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setBusy(true);
        setResult(undefined);
        setFound(undefined);
        api
          .get<ReportSummary>(`/api/expenses/settings/reports/${String(number)}`)
          .then(setFound)
          .catch((caught: unknown) => setResult({ ok: false, text: message(caught) }))
          .finally(() => setBusy(false));
      }}
    >
      <h2>Delete a report</h2>
      <p className="muted">
        Permanently deletes a report and everything on it: expenses, receipts, mileage and history.
        It can't be undone. A zip already archived for a paid report stays where it was saved.
      </p>
      <div className="delete-report-find">
        <label>
          Report number
          <input
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setFound(undefined);
              setResult(undefined);
            }}
            inputMode="numeric"
            placeholder="1042"
            autoComplete="off"
          />
        </label>
        <button className="btn secondary" disabled={busy || !valid}>
          {busy && !found ? "Looking…" : "Look up"}
        </button>
      </div>

      {found && (
        <div className="delete-report-found">
          <div className="row">
            <strong>
              #{found.number} · {found.title}
            </strong>
            <span>{money(found.totalUsd)}</span>
          </div>
          <span className="muted">
            {found.submitter.displayName} · {dateRange(found.periodStart, found.periodEnd)} ·{" "}
            {found.entryCount} expense{found.entryCount === 1 ? "" : "s"}
          </span>
          <StatusBadge report={found} />
          <button
            type="button"
            className="btn danger"
            disabled={busy}
            onClick={() => {
              void (async () => {
                const yes = await dialogs.confirm({
                  title: `Delete report #${String(found.number)}?`,
                  message:
                    `"${found.title}" from ${found.submitter.displayName}.\n\n` +
                    "Its expenses, receipts, mileage and history are permanently deleted. This can't be undone.",
                  confirmLabel: "Delete report",
                  danger: true,
                });
                if (!yes) return;
                setBusy(true);
                api
                  .del(
                    `/api/expenses/settings/reports/${String(found.number)}?confirm=${String(found.number)}`,
                  )
                  .then(() => {
                    setResult({ ok: true, text: `Report #${String(found.number)} was deleted.` });
                    setFound(undefined);
                    setTyped("");
                  })
                  .catch((caught: unknown) => setResult({ ok: false, text: message(caught) }))
                  .finally(() => setBusy(false));
              })();
            }}
          >
            <Icon name="trash" size={16} /> Delete report #{found.number}
          </button>
        </div>
      )}

      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
    </form>
  );
}
